import { AlertStatus, Infobox } from 'Atoms';
import {
  MaterialityState,
  MaterialityStatus,
  MetricMaterialityState,
  VSMEMaterialityStatus,
} from '../DoubleMaterialityAssessment.d';
import { LockedIcon } from 'Tokens/Icons/Function';
import { CheckIcon, InfoIcon, WarningIcon } from 'Tokens/Icons/Status';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment';

export const MATERIALITY_LABELS = {
  [MaterialityState.toAssess]: 'Select',
  [MaterialityState.notMaterial]: MaterialityStatus.notMaterial,
  [MaterialityState.material]: MaterialityStatus.material,
  [MaterialityState.collectData]: MaterialityStatus.collectData,
  [MaterialityState.gatherData]: MaterialityStatus.gatherData,
  [MaterialityState.doNotCollect]: MaterialityStatus.doNotCollect,
  [MaterialityState.phasedIn]: MaterialityStatus.phasedIn,
  [MaterialityState.doNotInclude]: MaterialityStatus.toAssess,
};

export const COLLECT_ONLY_OPTIONS = [
  {
    label: MaterialityStatus.collectData,
    value: MetricMaterialityState.collectData,
  },
  {
    label: MaterialityStatus.doNotCollect,
    value: MetricMaterialityState.doNotCollect,
  },
];

export const VSME_METRIC_MATERIALITY_OPTIONS = [
  {
    label: VSMEMaterialityStatus.material,
    value: MetricMaterialityState.material,
  },
  {
    label: VSMEMaterialityStatus.gatherData,
    value: MetricMaterialityState.gatherData,
  },
  {
    label: VSMEMaterialityStatus.notMaterial,
    value: MetricMaterialityState.notMaterial,
  },
];

export const METRIC_MATERIALITY_OPTIONS = [
  {
    label: MaterialityStatus.material,
    value: MetricMaterialityState.material,
  },
  {
    label: MaterialityStatus.notMaterial,
    value: MetricMaterialityState.notMaterial,
  },
  {
    label: MaterialityStatus.gatherData,
    value: MetricMaterialityState.gatherData,
  },
  {
    label: MaterialityStatus.phasedIn,
    value: MetricMaterialityState.phasedIn,
  },
];

export const DR_STATES = {
  [MaterialityState.mandatory]: {
    status: 'neutral' as AlertStatus,
    title: 'Mandatory',
    description: 'This disclosure requirement is mandatory for all companies',
    icon: <LockedIcon color="text.muted" />,
  },
  [MaterialityState.materialMandatory]: {
    status: 'info' as AlertStatus,
    title: 'Material',
    description: 'This disclosure requirement is material because topic was assessed as material',
    icon: <LockedIcon color="text.info" />,
  },
  [MaterialityState.material]: {
    status: 'info' as AlertStatus,
    title: MaterialityStatus.material,
    description: 'You have assessed this disclosure requirement as material',
    icon: <CheckIcon color="text.info" />,
  },
  [MaterialityState.notMaterial]: {
    status: 'neutral' as AlertStatus,
    title: MaterialityStatus.notMaterial,
    description: 'You have assessed this disclosure requirement as not material',
    icon: <CheckIcon color="inherit" />,
  },
  [MaterialityState.toAssess]: {
    status: 'warning' as AlertStatus,
    title: 'To assess',
    description: 'Learn more about the DR and assess if it is material for your company or not',
    icon: <WarningIcon color="text.warning" />,
  },
  [MaterialityState.gatherData]: {
    status: 'neutral' as AlertStatus,
    title: MaterialityStatus.gatherData,
    description:
      'You have assessed this disclosure requirement as not material; however, you will still be able to collect data for it.',
    icon: <CheckIcon color="inherit" />,
  },
  [MaterialityState.collectDataMandatory]: {
    status: 'info' as AlertStatus,
    title: 'Collect data',
    description:
      'This disclosure requirement is material for a parent, so it is required to collect data',
    icon: <LockedIcon color="text.info" />,
  },
  [MaterialityState.doNotCollect]: {
    status: 'info' as AlertStatus,
    title: 'Not required to collect data',
    description:
      'This DR is not material for a parent, so you don’t have to collect data. However, you can choose to collect data if you wish',
    icon: <InfoIcon mt="3px" />,
  },
  [MaterialityState.collectData]: {
    status: 'info' as AlertStatus,
    title: 'Collect data',
    description: 'You have assessed this disclosure requirement as material',
    icon: <CheckIcon color="text.info" />,
  },
  [MaterialityState.phasedIn]: {
    status: 'info' as AlertStatus,
    title: 'Phased-in',
    description: 'You have assessed this disclosure requirement as material but phased-in',
    icon: <CheckIcon color="text.info" />,
  },
  [MaterialityState.doNotInclude]: {
    status: 'neutral' as AlertStatus,
    title: 'Do no include',
    description: 'Learn more about the DR and assess if it is material for your company or not',
    icon: <CheckIcon color="inherit" />,
  },
};

const VSME_DR_STATES: Partial<
  Record<MaterialityState, Partial<(typeof DR_STATES)[MaterialityState]>>
> = {
  [MaterialityState.material]: {
    title: 'Collect and report',
    description: 'You have selected to collect and report data on the disclosures',
  },
  [MaterialityState.gatherData]: {
    title: 'Only collect',
    description: 'You have selected to collect data on the disclosures, but not report it',
  },
  [MaterialityState.notMaterial]: {
    title: 'Not collected',
    description: 'You have selected to not collect data on the disclosures',
  },
};

function getDrState(status: MaterialityState, framework: string) {
  const base = DR_STATES[status];
  if (framework === FrameworkRefs.vsme && VSME_DR_STATES[status]) {
    return { ...base, ...VSME_DR_STATES[status] };
  }
  return base;
}

export const DisclosureRequirementInfo = ({
  status,
  framework,
}: {
  status: MaterialityState;
  framework: string;
}) => {
  const drState = getDrState(status, framework);
  return (
    <Infobox
      status={drState.status}
      titleProps={{ variant: drState.status === 'info' ? 'h3' : 'h2' }}
      title={drState.title}
      description={drState.description}
      closable={false}
      textVariant="h3"
      icon={drState.icon}
    />
  );
};
